import { useState, useRef, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Box, Chip, Card, useTheme, Typography, Fade } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import { startCase } from 'lodash';
import { PageLoading } from './standard-loading';
import { PriceDisplay } from './price-display';


export function ModelPreviewCard({
    model,
    width,
    onClick = null,
    onImageLoaded = null,
    hideLoadingWheel = false
}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);
    const counter = useRef(0);

    const imageLoaded = () => {
        counter.current += 1;
        if (counter.current >= (model.images.preview || []).length) {
            setLoading(false);
            if (onImageLoaded) {
                onImageLoaded();
            }
        }
    };

    useEffect(() => {
        const previews = model.images.preview;
        if (!previews || previews.length === 0) {
            setLoading(false);
            if (onImageLoaded) {
                onImageLoaded();
            }
        }
    }, [model.images.preview, onImageLoaded]);

    const handleClick = () => {
        if (onClick) {
            onClick(model);
        } else {
            navigate(`/designs/${model.name}`);
        }
    };

    return (
        <Card
            onClick={handleClick}
            elevation={0}
            sx={{
                width: width || '100%',
                maxWidth: "100%",
                backgroundColor: '#FFFFFF',
                borderRadius: '18px',
                overflow: 'hidden',
                position: "relative",
                display: "flex",
                flexDirection: "column",
                cursor: 'pointer',
                transform: 'translate3d(0, 0, 0)',
                transition: 'transform 0.35s ease, box-shadow 0.35s ease',
                '&:hover': {
                    transform: 'translate3d(0, -2px, 0)',
                    boxShadow: '0 4px 14px rgba(33,31,28,.18)',
                },
                '& .card-image': {
                    willChange: 'transform',
                    transform: 'scale(1)',
                },
                '&:hover .card-image': {
                    transform: 'scale(1.02)',
                },
                '& .card-chevron': {
                    opacity: { xs: 1, md: 0 },
                    transition: 'opacity 0.2s ease',
                },
                '&:hover .card-chevron': {
                    opacity: 1,
                },
            }}
        >
            {/* Header Section: Title + Tags */}
            <Box sx={{ px: '22px', pt: '20px', pb: '14px', borderBottom: '1px solid', borderColor: 'line', transform: 'translateZ(0)', backfaceVisibility: 'hidden', position: 'relative', zIndex: 2 }}>
                <Typography
                    component="h3"
                    sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.25,
                    }}
                >
                    {startCase(model.name)}
                </Typography>

                {(model.header_tags || model.header) && (
                    Array.isArray(model.header_tags) ? (
                        <Typography
                            sx={{
                                color: 'ink3',
                                mt: 0.75,
                                fontSize: '13px',
                                lineHeight: 1.4,
                            }}
                        >
                            {model.header_tags.join(' · ')}
                        </Typography>
                    ) : model.header ? (
                        <Typography
                            sx={{
                                color: 'ink3',
                                mt: 0.75,
                                fontSize: '13px',
                                lineHeight: 1.4,
                            }}
                        >
                            {model.header}
                        </Typography>
                    ) : null
                )}
            </Box>

            {/* Image Section */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '1/1',
                    backgroundColor: theme.palette.paper2 || '#F2E9DC',
                    overflow: 'hidden',
                }}
            >
                {/* Difficulty badge — top right */}
                {model.difficulty && (
                    <Chip
                        label={startCase(model.difficulty)}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            zIndex: 2,
                            backgroundColor: 'rgba(255,255,255,.92)',
                            color: theme.palette.ink || '#211F1C',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 26,
                            backdropFilter: 'blur(4px)',
                        }}
                    />
                )}

                {/* Loading State */}
                {!hideLoadingWheel && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: loading ? "flex" : "none",
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1,
                    }}>
                        <PageLoading />
                    </Box>
                )}

                {/* Image Carousel or No-Image Placeholder */}
                <Fade in={!loading} timeout={{ enter: 500, exit: 250 }}>
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: loading ? "none" : "block"
                    }}>
                        {(model.images.preview || []).length > 0 ? (
                            <Box sx={{ height: '100%', position: 'relative' }}>
                                {model.images.preview.map((img, index) => (
                                    <img
                                        key={img.url || index}
                                        className="card-image"
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: 'contain',
                                            transition: 'transform 0.4s ease',
                                            display: index === activeIndex ? 'block' : 'none',
                                        }}
                                        src={img.url}
                                        alt={`${model.name}-preview`}
                                        onLoad={imageLoaded}
                                    />
                                ))}
                                {model.images.preview.length > 1 && (
                                    <>
                                        {/* Left/Right arrows */}
                                        <Box
                                            className="card-chevron"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveIndex((prev) =>
                                                    prev === 0 ? model.images.preview.length - 1 : prev - 1
                                                );
                                            }}
                                            sx={{
                                                position: 'absolute',
                                                left: 6,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                zIndex: 2,
                                                width: 30,
                                                height: 30,
                                                borderRadius: '50%',
                                                backgroundColor: 'rgba(255,255,255,0.85)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                                                '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                                            }}
                                        >
                                            <ChevronLeftIcon sx={{ fontSize: 20, color: 'text.primary' }} />
                                        </Box>
                                        <Box
                                            className="card-chevron"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveIndex((prev) =>
                                                    prev === model.images.preview.length - 1 ? 0 : prev + 1
                                                );
                                            }}
                                            sx={{
                                                position: 'absolute',
                                                right: 6,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                zIndex: 2,
                                                width: 30,
                                                height: 30,
                                                borderRadius: '50%',
                                                backgroundColor: 'rgba(255,255,255,0.85)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                                                '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                                            }}
                                        >
                                            <ChevronRightIcon sx={{ fontSize: 20, color: 'text.primary' }} />
                                        </Box>

                                        {/* Dot indicators */}
                                        <Box sx={{
                                            position: 'absolute',
                                            bottom: 10,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            display: 'flex',
                                            gap: '6px',
                                            zIndex: 2,
                                        }}>
                                            {model.images.preview.map((_, index) => (
                                                <Box
                                                    key={index}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setActiveIndex(index);
                                                    }}
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: index === activeIndex
                                                            ? theme.palette.primary.main
                                                            : 'rgba(0,0,0,0.25)',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s ease',
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: 'ink3',
                            }}>
                                <NoPhotographyIcon sx={{ fontSize: 64, mb: 1 }} />
                                <Typography variant="body2" sx={{ color: 'ink3' }}>
                                    No preview available
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Fade>
            </Box>

            {/* Separator */}
            <Box sx={{ height: '1px', backgroundColor: 'line', flexShrink: 0, transform: 'translateZ(0)', backfaceVisibility: 'hidden' }} />

            {/* Footer: Price + CTA */}
            <Box sx={{
                px: '22px',
                py: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: 'text.primary',
                    }}
                >
                    <PriceDisplay amount={model.amount} />
                </Typography>

                <Box
                    component="span"
                    sx={{
                        color: 'woodDark',
                        fontWeight: 600,
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                    }}
                >
                    Customize
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Box>
            </Box>
        </Card>
    );
}
